import { AnimatePresence, motion } from 'framer-motion';

import {
  AlertModal,
  BenefitDesignFilters,
  CoverageGapCard,
  CustomRangeSlider,
  CustomSlider,
  DEFAULT_FILTERS_VALUES,
  ELIGIBILITY_TOOLTIP_CONTENT,
  FilterDefaultsControls,
  formatBenefitDurationLabel,
  formatMinimumInflationLabel,
  InfoTooltip,
  MAX_INFLATION_PERCENT,
  ShortTermCareFilters,
  SHORT_TERM_CARE_PAYMENT_OPTION,
  TRADITIONAL_LTC_PAYMENT_OPTIONS,
  useBenefitTypeFilterActions,
  useCarrierExclusionsActions,
  useEligibilityFiltersActions,
  useFilterNoticeDialog,
  useHiddenCarriers,
  useIncludeCouplesDiscount,
  useSelectedBenefitPaymentType,
  useSelectedEligibilityFilters,
  useSelectedFeatureBenefitsFilters,
  useShopInsuranceContext,
  useShopInsuranceFilterDefaults,
  type BenefitPaymentTypeFilter,
  type BenefitDesignDraftFiltersController,
  type CombinedFilterType,
  type InflationTypeFilterValue,
  type PaymentOption,
  type ShopInsuranceFiltersProps,
  type ShopInsuranceMode,
} from '@/features/shop-insurance';
import {
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@design-system';

import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { formatCurrency } from '@/lib';
import {
  selectClient,
  selectQuotes,
  selectSession,
  selectSessionAdvisor,
  selectTraditionalLTCQuotes,
} from '@/model';
import { isWizard, SessionType, UNDERWRITING_STATUS_FILTERS, zipCodeToState } from '@shared';

export type ShopInsuranceFiltersComponentProps = ShopInsuranceFiltersProps & {
  benefitDesignDraftFilters?: BenefitDesignDraftFiltersController;
};

export function ShopInsuranceFilters({
  showEligibilityFilter = true,
  eligibilityCounts,
  mode = 'premium-optimization',
  handleModeChange,
  benefitDesignAvailableOptions,
  benefitDesignDraftFilters,
  availableCarriers = [],
  onMobileClose,
  visibleQuoteCount,
}: ShopInsuranceFiltersComponentProps = {}) {
  const { flags } = useFeatureFlags();
  const client = useSelector(selectClient);
  const state = zipCodeToState(client.intakeSurvey.clientZipCode);
  const { showNotice, handleNoticeChange, handleConfirm, handleNoticeOpenChange } =
    useFilterNoticeDialog();
  const { setValue, getValues, watch, control } = useFormContext<any>();
  const {
    sliderBounds,
    paymentOption,
    handlePaymentOptionChange,
    showTraditionalLTCOptions,
    showShortTermCareOption,
    showSinglePayAnd10Pay,
    show20Pay,
    isGapFillMode,
    setGapFillMode,
  } = useShopInsuranceContext();

  const { sessionType } = useSelector(selectSession);
  const isAdvisor = sessionType === SessionType.advisor || sessionType === SessionType.wizard;

  const advisor = useSelector(selectSessionAdvisor);
  const isWaterlilyUser = isWizard(advisor);
  const returnedLifeQuotes = useSelector(selectQuotes);
  const returnedTraditionalQuotes = useSelector(selectTraditionalLTCQuotes);
  const hasCouplesDiscountQuotes = returnedLifeQuotes.some(
    quote => quote.policyType === 'Couples Discount',
  );

  const selectedEligibilityFilters = useSelectedEligibilityFilters();
  const selectedFeatureBenefitsFilters = useSelectedFeatureBenefitsFilters();
  const includeCouplesDiscount = useIncludeCouplesDiscount();
  const {
    setSelectedEligibilityFilters,
    setSelectedFeatureBenefitsFilters,
    setIncludeCouplesDiscount,
  } = useEligibilityFiltersActions();
  const hiddenCarriers = useHiddenCarriers();
  const { toggleCarrier } = useCarrierExclusionsActions();
  const selectedBenefitPaymentType = useSelectedBenefitPaymentType();
  const { setSelectedBenefitPaymentType } = useBenefitTypeFilterActions();

  const filterDefaults = useShopInsuranceFilterDefaults({
    mode,
    paymentOption,
    getValues,
  });

  const benefitDurationValue: CombinedFilterType = watch('benefitDuration');
  const inflationProtectionRangeValue: CombinedFilterType = watch('inflationProtectionRange');
  const inflationTypeFilterValue: InflationTypeFilterValue = watch('inflationTypeFilter');
  const useMinimumInflation = flags.inflationProtectionMinimum ?? false;

  // Shared by both inflation filters, so the control is identical whichever one is shown.
  const inflationTypeSelect = (
    <div className="space-y-1.5">
      <Label className="text-xs">Inflation Type</Label>
      <Select
        value={inflationTypeFilterValue}
        onValueChange={value => setValue('inflationTypeFilter', value)}
      >
        <SelectTrigger
          className="w-full"
          data-testid="shop-insurance-filters-inflation-type"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All</SelectItem>
          <SelectItem value="simple">Simple</SelectItem>
          <SelectItem value="compound">Compound</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );

  // The short-term care view brings its own filters and shows neither life nor
  // traditional-LTC quotes, so the life filter stack does not apply to it.
  const isShortTermCareView = paymentOption === SHORT_TERM_CARE_PAYMENT_OPTION;
  const showFilters =
    !TRADITIONAL_LTC_PAYMENT_OPTIONS.includes(paymentOption) && !isShortTermCareView;

  // On Annually/Monthly the screen shows traditional LTC quotes (a separate slice);
  // otherwise it shows the life/single quotes.
  const returnedQuoteCount = showFilters
    ? returnedLifeQuotes.length
    : returnedTraditionalQuotes.length;

  function handleFillGapClick() {
    setGapFillMode(true);
  }

  const eligibilityFilters = UNDERWRITING_STATUS_FILTERS;

  const handleEligibilityFilterToggle = (filterValue: string) => {
    const updatedFilters = selectedEligibilityFilters.includes(filterValue)
      ? selectedEligibilityFilters.filter(filter => filter !== filterValue)
      : [...selectedEligibilityFilters, filterValue];
    setSelectedEligibilityFilters(updatedFilters);
  };

  const handleFeatureBenefitsFilterToggle = (filterValue: string) => {
    const updatedFilters = selectedFeatureBenefitsFilters.includes(filterValue)
      ? selectedFeatureBenefitsFilters.filter(filter => filter !== filterValue)
      : [...selectedFeatureBenefitsFilters, filterValue];
    setSelectedFeatureBenefitsFilters(updatedFilters);
  };

  const isBenefitDesign = mode === 'benefit-design';

  const showByBenefitFilters =
    isBenefitDesign &&
    benefitDesignAvailableOptions &&
    benefitDesignDraftFilters &&
    !isShortTermCareView;

  return (
    <div className="space-y-5">
      {handleModeChange && !isShortTermCareView && (
        <Tabs
          value={mode}
          onValueChange={value => handleModeChange(value as ShopInsuranceMode)}
        >
          <TabsList className="w-full">
            <TabsTrigger
              value="premium-optimization"
              className="flex-1"
            >
              By Premium
            </TabsTrigger>
            <TabsTrigger
              value="benefit-design"
              className="flex-1"
            >
              By Benefit
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {showByBenefitFilters && (
        <BenefitDesignFilters
          availableOptions={benefitDesignAvailableOptions}
          availableCarriers={availableCarriers}
          draftFilters={benefitDesignDraftFilters}
          onMobileClose={onMobileClose}
        />
      )}

      {!isBenefitDesign && showFilters && (
        <AnimatePresence initial={false}>
          {!isGapFillMode && (
            <motion.div
              initial={{ opacity: 0, height: 0, overflow: 'hidden' }}
              animate={{ opacity: 1, height: 'auto', overflow: 'visible' }}
              exit={{ opacity: 0, height: 0, overflow: 'hidden' }}
              transition={{ duration: 0.2 }}
            >
              <h3 className="text-primary-dark mb-3 text-sm font-semibold">Target Premium</h3>
              <CustomSlider
                name="premium"
                control={control}
                max={sliderBounds.max}
                min={sliderBounds.min}
                step={1000}
                defaultValue={sliderBounds.defaultValue}
                formatValue={value => formatCurrency(value)}
                hideResetButton={false}
                defaultLabel="Reset"
                label={value =>
                  formatCurrency(Math.max(sliderBounds.min, Math.min(sliderBounds.max, value)))
                }
                showCustomInput={flags.customPremiumInput ?? false}
                customInputLabel="Enter custom amount"
                defaultCustomInputChecked={watch('useCustomPremiumInput') ?? false}
                onCustomInputChange={checked => setValue('useCustomPremiumInput', checked)}
                data-testid="shop-insurance-filters-premium-min"
                headerActions={
                  flags.coverageGap ? (
                    <Button
                      onClick={handleFillGapClick}
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs"
                    >
                      Fill Coverage Gap
                    </Button>
                  ) : undefined
                }
              />
            </motion.div>
          )}
        </AnimatePresence>
      )}
      {flags.coverageGap && !isBenefitDesign && showFilters && isGapFillMode && <CoverageGapCard />}
      {/* Always rendered in the short-term care view: that view ignores mode, so
          hiding the selector under "By Benefit" would strand the advisor with STC
          cards and no way to switch products. */}
      {(!isBenefitDesign || isShortTermCareView) && (
        <div>
          <h3 className="text-primary-dark mb-2 text-sm font-semibold">Payment Option</h3>
          <Select
            value={paymentOption}
            onValueChange={value => handlePaymentOptionChange(value as PaymentOption)}
          >
            <SelectTrigger
              className="w-full"
              data-testid={`shop-insurance-filters-payment-option`}
            >
              <SelectValue placeholder="Select payment option" />
            </SelectTrigger>
            <SelectContent>
              {showSinglePayAnd10Pay && (
                <>
                  <SelectItem value="Single Pay">Single Pay</SelectItem>
                  <SelectItem value="10-Pay">10-Pay</SelectItem>
                </>
              )}
              {/* Separate gate from Single/10-Pay: we hold 20-Pay rows for only a
                  few demographic cohorts, so this must not inherit the blanket
                  age rule or most clients would select it and get no quotes. */}
              {show20Pay && <SelectItem value="20-Pay">20-Pay</SelectItem>}
              {showTraditionalLTCOptions && (
                <>
                  <SelectItem value="Annually">Annually</SelectItem>
                  <SelectItem value="Monthly">Monthly</SelectItem>
                </>
              )}
              {showShortTermCareOption && (
                <SelectItem value="Short-Term Care">Monthly — Short-Term Care</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
      )}
      {/* Short-term care shops on its own axes; the component reads its own
          store, so no STC state is threaded through the shared sidebar. */}
      {isShortTermCareView && (
        <div className="border-border border-t pt-5">
          <ShortTermCareFilters />
        </div>
      )}
      {!isBenefitDesign && !isShortTermCareView && availableCarriers.length > 0 && (
        <div className="border-border border-t pt-5">
          <h3 className="text-primary-dark mb-2 text-sm font-semibold">Carriers</h3>
          <DropdownMenu>
            <DropdownMenuTrigger className="w-full focus-visible:outline-none">
              <Button
                variant="outline"
                className="w-full justify-between font-normal"
                data-testid="shop-insurance-filters-carriers-dropdown"
              >
                <span className="truncate">
                  {(() => {
                    const relevantHidden = hiddenCarriers.filter(c =>
                      availableCarriers.includes(c),
                    );
                    return relevantHidden.length === 0
                      ? 'All Carriers'
                      : `${availableCarriers.length - relevantHidden.length} of ${availableCarriers.length} selected`;
                  })()}
                </span>
                <svg
                  className="size-4 shrink-0 opacity-50"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-(--radix-dropdown-menu-trigger-width)"
              align="start"
            >
              {availableCarriers.map(carrier => (
                <DropdownMenuCheckboxItem
                  key={carrier}
                  checked={!hiddenCarriers.includes(carrier)}
                  onCheckedChange={() => toggleCarrier(carrier)}
                  onSelect={e => e.preventDefault()}
                  data-testid={`shop-insurance-filters-carrier-${carrier}`}
                >
                  {carrier}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
      {!isBenefitDesign && showFilters && (
        <div className="border-border border-t pt-5">
          <h3 className="text-primary-dark mb-2 text-sm font-semibold">Benefit Type</h3>
          <Select
            value={selectedBenefitPaymentType}
            onValueChange={value =>
              setSelectedBenefitPaymentType(value as BenefitPaymentTypeFilter)
            }
          >
            <SelectTrigger
              className="w-full"
              data-testid="shop-insurance-filters-benefit-type"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="reimbursement">Reimbursement</SelectItem>
              <SelectItem value="indemnity">Indemnity</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      {!isBenefitDesign && showFilters && (
        <div className="border-border border-t pt-5">
          {useMinimumInflation ? (
            <>
              <h3 className="text-primary-dark mb-3 text-sm font-semibold">Inflation Protection</h3>
              <div className="space-y-3">
                <CustomSlider
                  min={0}
                  max={MAX_INFLATION_PERCENT}
                  step={1}
                  control={control}
                  name="inflationProtection"
                  defaultValue={DEFAULT_FILTERS_VALUES.inflationProtection}
                  defaultLabel="Reset"
                  labelSuffix="%"
                  updateClient={() => handleNoticeChange('inflationProtection')}
                  label={value => (
                    <span className="text-sm">{formatMinimumInflationLabel(value)}</span>
                  )}
                  data-testid="shop-insurance-filters-inflation-protection"
                />
                <p className="text-muted-foreground text-xs">
                  Minimum protection. A policy qualifies if its AOB or COB rate reaches this
                  percentage.
                </p>
                {inflationTypeSelect}
              </div>
            </>
          ) : (
            <>
              <div className="mb-3 flex items-center justify-between gap-2">
                <h3 className="text-primary-dark text-sm font-semibold">Inflation Protection</h3>
                <Select
                  value={inflationProtectionRangeValue.type}
                  onValueChange={value =>
                    setValue('inflationProtectionRange', {
                      ...inflationProtectionRangeValue,
                      type: value,
                    })
                  }
                >
                  <SelectTrigger
                    size="sm"
                    data-testid="shop-insurance-filters-inflation-protection-select"
                    className="h-8! w-max px-2 py-1.5 text-xs [&>svg]:size-3.5"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="combined">Combined</SelectItem>
                    <SelectItem value="separate">Separate AOB/COB</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {inflationProtectionRangeValue.type === 'combined' ? (
                <div className="space-y-3">
                  <CustomRangeSlider
                    showTooltip
                    min={0}
                    max={MAX_INFLATION_PERCENT}
                    step={1}
                    control={control}
                    name="inflationProtectionRange.combined"
                    tooltipContent={value => `${value}%`}
                    defaultValue={DEFAULT_FILTERS_VALUES.inflationProtectionRange}
                    onValueChange={() => handleNoticeChange('inflationProtection')}
                    label={`${inflationProtectionRangeValue.combined[0]}% - ${inflationProtectionRangeValue.combined[1]}%`}
                    testId="shop-insurance-filters-inflation-protection-combined"
                  />
                  <div className="pt-3">{inflationTypeSelect}</div>
                </div>
              ) : (
                <div className="flex w-full gap-4">
                  <div className="w-full">
                    <h4 className="text-primary-dark mb-2 text-xs font-medium">AOB</h4>
                    <CustomRangeSlider
                      showTooltip
                      min={0}
                      max={MAX_INFLATION_PERCENT}
                      step={1}
                      size="sm"
                      control={control}
                      name="inflationProtectionRange.aob"
                      tooltipContent={value => `${value}%`}
                      defaultValue={DEFAULT_FILTERS_VALUES.inflationProtectionRange}
                      onValueChange={() => handleNoticeChange('inflationProtection')}
                      label={`${inflationProtectionRangeValue.aob[0]}% - ${inflationProtectionRangeValue.aob[1]}%`}
                      testId="shop-insurance-filters-inflation-protection-aob"
                    />
                  </div>
                  <div className="w-full">
                    <h4 className="text-primary-dark mb-2 text-xs font-medium">COB</h4>
                    <CustomRangeSlider
                      showTooltip
                      min={0}
                      max={MAX_INFLATION_PERCENT}
                      step={1}
                      size="sm"
                      control={control}
                      name="inflationProtectionRange.cob"
                      tooltipContent={value => `${value}%`}
                      defaultValue={DEFAULT_FILTERS_VALUES.inflationProtectionRange}
                      onValueChange={() => handleNoticeChange('inflationProtection')}
                      label={`${inflationProtectionRangeValue.cob[0]}% - ${inflationProtectionRangeValue.cob[1]}%`}
                      testId="shop-insurance-filters-inflation-protection-cob"
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
      {!isBenefitDesign && showFilters && (
        <div className="border-border border-t pt-5">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h3 className="text-primary-dark text-sm font-semibold">Benefit Duration</h3>
            <Select
              value={benefitDurationValue.type}
              onValueChange={value =>
                setValue('benefitDuration', { ...benefitDurationValue, type: value })
              }
            >
              <SelectTrigger className="h-8! w-max px-2 py-1.5 text-xs [&>svg]:size-3.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="combined">Total Duration</SelectItem>
                <SelectItem value="separate">Separate AOB/COB</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {benefitDurationValue.type === 'combined' ? (
            <CustomRangeSlider
              showTooltip
              min={1}
              max={9}
              step={1}
              control={control}
              name="benefitDuration.combined"
              tooltipContent={value =>
                value === 9 ? 'Lifetime' : `${value} Year${value !== 1 ? 's' : ''}`
              }
              defaultValue={DEFAULT_FILTERS_VALUES.benefitDuration}
              onValueChange={() => handleNoticeChange('benefitDuration')}
              label={formatBenefitDurationLabel(
                benefitDurationValue.combined[0],
                benefitDurationValue.combined[1],
              )}
              testId="shop-insurance-filters-benefit-duration-combined"
            />
          ) : (
            <div className="flex w-full gap-2">
              <div className="w-full">
                <h4 className="text-primary-dark mb-2 text-xs font-medium">AOB</h4>
                <CustomRangeSlider
                  showTooltip
                  size="sm"
                  min={1}
                  step={1}
                  max={9}
                  control={control}
                  name="benefitDuration.aob"
                  tooltipContent={value =>
                    value === 9 ? 'Lifetime' : `${value} Year${value !== 1 ? 's' : ''}`
                  }
                  defaultValue={DEFAULT_FILTERS_VALUES.benefitDuration}
                  onValueChange={() => handleNoticeChange('benefitDuration')}
                  label={formatBenefitDurationLabel(
                    benefitDurationValue.aob[0],
                    benefitDurationValue.aob[1],
                  )}
                  testId="shop-insurance-filters-benefit-duration-aob"
                />
              </div>
              <div className="w-full">
                <h4 className="text-primary-dark mb-2 text-xs font-medium">COB</h4>
                <CustomRangeSlider
                  size="sm"
                  showTooltip
                  min={1}
                  step={1}
                  max={9}
                  control={control}
                  name="benefitDuration.cob"
                  tooltipContent={value =>
                    value === 9 ? 'Lifetime' : `${value} Year${value !== 1 ? 's' : ''}`
                  }
                  defaultValue={DEFAULT_FILTERS_VALUES.benefitDuration}
                  onValueChange={() => handleNoticeChange('benefitDuration')}
                  label={formatBenefitDurationLabel(
                    benefitDurationValue.cob[0],
                    benefitDurationValue.cob[1],
                  )}
                  testId="shop-insurance-filters-benefit-duration-cob"
                />
              </div>
            </div>
          )}
        </div>
      )}
      {!isBenefitDesign && showFilters && hasCouplesDiscountQuotes && (
        <div className="border-border border-t pt-5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-1 flex-col">
              <Label
                htmlFor="include-couples-discount"
                className="text-primary-dark cursor-pointer text-sm font-semibold"
              >
                Couples Discount
              </Label>
              <span className="text-muted-foreground text-xs">
                Include spousal-discount quotes in the results
              </span>
            </div>
            <Switch
              id="include-couples-discount"
              checked={includeCouplesDiscount}
              onCheckedChange={setIncludeCouplesDiscount}
              data-testid="shop-insurance-filters-couples-discount"
            />
          </div>
        </div>
      )}
      {/* Both counters describe the life and traditional-LTC slices; neither is
          on screen in the short-term care view. */}
      {isWaterlilyUser && !isShortTermCareView && (
        <div
          className="border-border border-t pt-5"
          data-testid="shop-insurance-filters-returned-quotes"
        >
          <h3 className="text-primary-dark mb-1 text-sm font-semibold">Returned Quotes</h3>
          <p className="text-muted-foreground text-xs">
            Admin only · {returnedQuoteCount} returned · {visibleQuoteCount ?? 0} visible
          </p>
        </div>
      )}
      {!isBenefitDesign && !isShortTermCareView && showEligibilityFilter && (
        <div className="border-border border-t pt-5">
          <h3 className="text-primary-dark mb-3 text-sm font-semibold">Eligibility Filter</h3>
          <div className="flex flex-col gap-3">
            {eligibilityFilters.map(filter => {
              const count =
                eligibilityCounts?.[filter.value as keyof typeof eligibilityCounts] || 0;
              return (
                <div
                  key={filter.value}
                  className="flex items-center gap-3"
                >
                  <Checkbox
                    data-testid={`shop-insurance-filters-eligibility-${filter.value}`}
                    id={filter.value}
                    checked={selectedEligibilityFilters.includes(filter.value)}
                    onCheckedChange={() => handleEligibilityFilterToggle(filter.value)}
                  />
                  <label
                    htmlFor={filter.value}
                    className="text-foreground flex flex-1 cursor-pointer items-center gap-1.5 text-sm font-medium"
                  >
                    {filter.label}
                    <span className="text-foreground text-xs">({count})</span>
                  </label>
                  <InfoTooltip content={ELIGIBILITY_TOOLTIP_CONTENT[filter.value] || ''} />
                </div>
              );
            })}
          </div>
        </div>
      )}
      {!isBenefitDesign &&
        !isShortTermCareView &&
        !['CA', 'NY'].includes(state.stateAbbreviation) && (
          <div className="border-border border-t pt-5">
            <h3 className="text-primary-dark mb-3 text-sm font-semibold">Features & Benefits</h3>
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <Checkbox
                  data-testid="shop-insurance-filters-global-benefits"
                  id="hasGlobalBenefits"
                  checked={selectedFeatureBenefitsFilters.includes('hasGlobalBenefits')}
                  onCheckedChange={() => handleFeatureBenefitsFilterToggle('hasGlobalBenefits')}
                />
                <label
                  htmlFor="hasGlobalBenefits"
                  className="text-foreground flex flex-1 cursor-pointer items-center gap-1.5 text-sm font-medium"
                >
                  International Benefits
                </label>
              </div>
            </div>
          </div>
        )}
      {isAdvisor && flags.persistentFilters && !isShortTermCareView && (
        <FilterDefaultsControls
          isSaving={filterDefaults.isSaving}
          isSuccess={filterDefaults.isSuccess}
          onSave={filterDefaults.saveCurrentAsDefaults}
        />
      )}
      <AlertModal
        title="Are you sure?"
        open={showNotice}
        onOpenChange={handleNoticeOpenChange}
        onConfirm={handleConfirm}
      >
        By adjusting this filter you are reducing the number of policies analyzed, which may filter
        out policies with a high modeled ROI, leaving you with lower modeled ROI options. Are you
        sure you want to restrict the search?
      </AlertModal>
    </div>
  );
}
